import { TextInput } from "react-admin";
import { Student } from "../providers/studentsProvider";
import { LoadingButton } from "@mui/lab";
import UndoIcon from "@mui/icons-material/Undo";
import { DialogProps } from "../misc/DialogProps";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { reportError } from "../backoffice.utils";
import { Invoice } from "../providers/invoicesProvider";
import { moneyFormatter } from "../misc/Money";
import { getAuthenticatedBackendClient } from "../api/backend.api";
import { FormProvider, useForm } from "react-hook-form";
import { setStudentStatus } from "../api/backoffice.api";
import { useQueryClient } from "react-query";
import { DialogCloseButton } from "../misc/DialogCloseButton";

export function BookOutDunningDialog({
  open,
  onClose,
  student,
  bookOutDunningMeta,
}: DialogProps & {
  student: Student;
  bookOutDunningMeta: { invoices?: Array<Invoice> };
}) {
  const formProps = useForm<{ note: string }>({
    defaultValues: { note: "Offene Posten ausgebucht. Kunde abgebrochen, keine Leistungen in Anspruch genommen." },
  });
  const queryClient = useQueryClient();
  const invoices: Array<Invoice> = bookOutDunningMeta.invoices || [];

  const bookOut = async ({ note }: { note: string }) => {
    let success = false;
    try {
      if (invoices.length === 0) {
        throw new Error("Unexpected state: invoices.length === 0");
      }
      const backendClient = await getAuthenticatedBackendClient();
      for (const invoice of invoices) {
        await backendClient.invoice.invoiceRefundCreate({ id: invoice.id });
      }
      await setStudentStatus(student, "cancelled", note); // <-- this will create the note on the student
      success = true;
    } catch (error) {
      reportError("Failed to book out student", error);
      showErrorToast(`Fehler beim Ausbuchen von ${student.name}`);
    }
    if (success) {
      onClose();
      showSuccessToast(
        `Alle offenen Rechnungen von ${student.name} wurden ausgebucht, der Status auf Abgebrochen geändert und die Notiz erstellt.`,
      );
      await queryClient.invalidateQueries(["students"]);
      await queryClient.invalidateQueries(["dunning", "candidateStudents"]);
      await queryClient.invalidateQueries(["studentNotes"]);
    }
  };

  return (
    <FormProvider {...formProps}>
      <form>
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
          <DialogTitle>{student.name} ausbuchen</DialogTitle>
          <DialogCloseButton onClick={onClose} disabled={formProps.formState.isSubmitting} />
          <DialogContent>
            <DialogContentText>Folgende offene Rechnungen werden ausgebucht:</DialogContentText>
            <table>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td style={{ paddingRight: "1em" }}>
                      <ul>
                        <li>{`Rechnung ${invoice.nr} ${invoice.type}`}</li>
                      </ul>
                    </td>
                    <td align="right">{moneyFormatter.format(invoice.total / 100)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TextInput style={{ marginTop: "1em", width: "100%" }} source="note" label="Notiz" multiline rows={3} />
          </DialogContent>
          <DialogActions>
            <LoadingButton
              loading={formProps.formState.isSubmitting}
              variant="contained"
              startIcon={<UndoIcon />}
              disabled={formProps.formState.isSubmitting}
              onClick={formProps.handleSubmit(bookOut)}
              autoFocus
            >
              Jetzt ausbuchen
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </form>
    </FormProvider>
  );
}
